import React, { Component } from 'react'
import { Icon, Tag, Tooltip } from 'antd'
import { subscriptions } from '@services/api'
import {
    STATUS_HEX,
    dupCloseState,
    dupDate,
    dupScanIssue,
    dupView,
    scanAge,
    shouldLoadStoHistory,
    stoRefreshNeedsHistory,
    stoRequestIsCurrent
} from '@pages/Tranzila/utils'
import StoDuplicatesModal from '@components/Popup/StoDuplicatesModal'

const BG = { red: '#fff1f0', green: '#f6ffed', grey: '#fafafa', blue: '#e6f7ff' }

// бейдж не меняет цвет карточки: "данные не обновляются" — не тот же факт, что "есть дубли"
const badge = d => {
    const issue = dupScanIssue(d)
    if (issue === 'stuck')
        return {
            text: 'הסריקה נתקעה',
            tip: `הסריקה החלה ב-${dupDate(d.last_attempt && d.last_attempt.scanned_at)} ולא הסתיימה`
        }
    if (issue === 'missed')
        return {
            text: 'הסריקה לא רצה',
            tip: `הסריקה האחרונה: ${dupDate(d.scanned_at)} · צפוי: ${dupDate(d.expected_last_run_at)}`
        }
    if (issue === 'failed') {
        const la = d.last_attempt
        return {
            text: 'הסריקה האחרונה נכשלה',
            tip: dupDate(la.scanned_at) + (la.error_message ? ` · ${la.error_message}` : '')
        }
    }
    return null
}

class StoDuplicatesWidget extends Component<any, any> {
    // showResolved живёт здесь, а не в модалке: включение чекбокса должно уметь дозагрузить
    // историю, а два владельца одного факта разъехались бы. historyLoaded — кэш на время жизни
    // виджета: повторные вкл/выкл не гоняют разбор старых сканов заново.
    state = {
        data: null,
        loading: true,
        error: false,
        open: false,
        hover: false,
        showResolved: false,
        // historyLoaded — история уже в data; historyLoading — запрос за ней в полёте.
        // Это разные факты: один флаг на оба давал второй запрос на OFF→ON во время первого.
        historyLoaded: false,
        historyLoading: false
    }

    // Поколение запроса. Ответы сети приходят в произвольном порядке, поэтому применяем только
    // ПОСЛЕДНИЙ ЗАПУЩЕННЫЙ: иначе старый ответ (снятый до записи) затирал бы свежий после неё.
    statusRequestId = 0

    componentDidMount() {
        this.fetch()
    }

    // данные меняются раз в сутки — поллинга нет, только mount, включение истории и
    // после resolve/unresolve. Без явного аргумента режим выбирает stoRefreshNeedsHistory:
    // после записи история обязана перезапроситься, иначе снятая резолюция осталась бы строкой.
    fetch = includeHistory => {
        const withHistory =
            includeHistory === undefined ? stoRefreshNeedsHistory(this.state) : !!includeHistory
        const requestId = ++this.statusRequestId
        if (withHistory) this.setState({ historyLoading: true })

        return subscriptions
            .getStoDuplicatesStatus(withHistory ? { include_history: true } : undefined)
            .then(res => {
                // Устаревший ответ не трогает НИЧЕГО: ни data, ни флаги загрузки, ни кэш.
                if (!stoRequestIsCurrent(requestId, this.statusRequestId)) return
                this.setState({
                    data: res.data,
                    loading: false,
                    error: false,
                    historyLoading: false,
                    // Флаг описывает те данные, что мы держим: ответ без истории её и не содержит.
                    historyLoaded: withHistory
                })
            })
            .catch(e => {
                console.log(e && e.response && e.response.data)
                if (!stoRequestIsCurrent(requestId, this.statusRequestId)) return
                // historyLoaded намеренно не трогаем: упавшее обновление не повод стирать
                // уже загруженный годный кэш.
                this.setState({ loading: false, error: true, historyLoading: false })
            })
    }

    handleShowResolvedChange = checked => {
        this.setState({ showResolved: checked })
        if (checked && shouldLoadStoHistory(this.state)) this.fetch(true)
    }

    // Каждое открытие начинается с дефолтного вида: чекбокс снят, история скрыта.
    // historyLoaded и загруженные history_users при этом сохраняются — см. dupCloseState.
    handleClose = () => this.setState(dupCloseState())

    card = ({ color, main, sub, spin = false, tag = null, onClick = null }) => (
        <div style={{ minWidth: 260 }}>
            <div
                className="sto-duplicates-card"
                onClick={onClick}
                onMouseEnter={() => this.setState({ hover: true })}
                onMouseLeave={() => this.setState({ hover: false })}
                style={{
                    minWidth: 260,
                    padding: '0.45rem 0.9rem',
                    borderRadius: 6,
                    border: `1px solid ${STATUS_HEX[color]}`,
                    background: BG[color],
                    cursor: onClick ? 'pointer' : 'default',
                    boxShadow: onClick && this.state.hover ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                    transition: 'box-shadow 0.2s'
                }}
            >
                <div style={{ fontWeight: 600, color: STATUS_HEX[color] }}>
                    {spin && <Icon type="loading" style={{ marginLeft: 6 }} />}
                    {main}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 22, marginTop: 4 }}>
                    {tag && (
                        <Tooltip title={tag.tip}>
                            <Tag
                                className="sto-duplicates-badge"
                                color="orange"
                                style={{ margin: '0 0 0 0.5rem', whiteSpace: 'nowrap' }}
                            >
                                {tag.text}
                            </Tag>
                        </Tooltip>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#8c8c8c', whiteSpace: 'nowrap' }}>{sub}</div>
                </div>
            </div>
        </div>
    )

    render() {
        const { data, loading, error, open, showResolved } = this.state
        if (loading) return this.card({ color: 'grey', main: 'סטטוס כפילויות', sub: 'טוען…', spin: true })
        if (error || !data) return this.card({ color: 'grey', main: 'שגיאה בטעינת סטטוס הכפילויות', sub: '—' })

        const v = dupView(data)
        return (
            <>
                {this.card({
                    color: v.color,
                    main: v.text,
                    sub: `עודכן ${scanAge(data.scanned_at)}`,
                    spin: v.spin,
                    tag: badge(data),
                    onClick: () => this.setState({ open: true })
                })}
                <StoDuplicatesModal
                    visible={open}
                    data={data}
                    showResolved={showResolved}
                    onShowResolvedChange={this.handleShowResolvedChange}
                    onRefresh={this.fetch}
                    onClose={this.handleClose}
                />
            </>
        )
    }
}

export default StoDuplicatesWidget
